import { default as wasm } from '../../xert-wasm/build/xert.js';
import type { MainModule as LibXert } from '../../xert-wasm/interface.d.ts';

export let libxert: LibXert;
export let wasmPromise;
try {
  wasmPromise = wasm(process.env.DEBUG ? {} : {print: (()=>{}), printErr: (()=>{})})
  .catch((e)=>{
    console.error(e);
    libxert = null;
  })
  .then((instance)=>{
    libxert = instance;
  });
} catch (e) {
  libxert = null;
}
export async function getLibxert() {
  await wasmPromise
  return libxert
}

let libxertWorkerDelegate: LibXert
let libxertWorker: Worker

// The capacity simulator (assess_capacity, capacity_rates) in a worker of its own: off the page's thread, so the sliders
// that start it don't freeze, and apart from the forecast's worker, which killWorker stops mid-forecast. One call at a
// time; a call with a key replaces a call with that key still waiting its turn, which resolves to null (its caller has
// moved on). Without a worker the calls run on the page's thread, as they do when the worker doesn't answer within
// CAPACITY_TIMEOUT (a worker script from before these calls, which ignores them).
const CAPACITY_TIMEOUT = 20000
type CapacityCall = { name: "assess_capacity" | "capacity_rates", args: any, key?: string, resolve: (result: any) => void, reject: (error: any) => void }
let capacityWorker: Worker | null = null
let capacityWorkerFailed = false
let capacityQueue: CapacityCall[] = []
let capacityBusy = false
let capacityInFlight: CapacityCall | null = null

// A worker that fails (it can't load, or doesn't answer) gives way to the page's thread, the call it had too
function giveUpCapacityWorker() {
  capacityWorkerFailed = true
  capacityWorker?.terminate()
  capacityWorker = null
  if (capacityInFlight) capacityQueue.unshift(capacityInFlight)
  capacityInFlight = null
  capacityBusy = false
  runCapacityQueue()
}

async function runCapacityQueue() {
  if (capacityBusy || !capacityQueue.length) return
  const call = capacityQueue.shift()!
  capacityBusy = true
  const done = (ok: boolean, value: any) => {
    capacityBusy = false
    capacityInFlight = null
    ok ? call.resolve(value) : call.reject(value)
    runCapacityQueue()
  }
  if (!capacityWorkerFailed && !capacityWorker) {
    try {
      capacityWorker = new Worker(process.env.LIBXERT_WORKER_URL)
      capacityWorker.onerror = giveUpCapacityWorker
    } catch (e) {
      capacityWorkerFailed = true
    }
  }
  if (capacityWorkerFailed || !capacityWorker) {
    try { done(true, (await getLibxert())[call.name](call.args)) } catch (e) { done(false, e) }
    return
  }
  const channel = new MessageChannel()
  capacityInFlight = call
  const timeout = setTimeout(() => { if (capacityInFlight === call) giveUpCapacityWorker() }, CAPACITY_TIMEOUT)
  channel.port1.onmessage = ({data}) => {
    clearTimeout(timeout)
    channel.port1.close()
    if (capacityInFlight !== call) return
    // An error from a worker that doesn't know the call: the page's thread does it
    if (data.error && /^Unknown call/.test(String(data.error))) return giveUpCapacityWorker()
    data.error ? done(false, data.error) : done(true, data.result)
  }
  capacityWorker.postMessage({name: call.name, args: [call.args]}, [channel.port2])
}

export function capacityCall(name: CapacityCall["name"], args: any, key?: string): Promise<any> {
  return new Promise((resolve, reject) => {
    if (key) capacityQueue = capacityQueue.filter(c => {
      if (c.key !== key) return true
      c.resolve(null)
      return false
    })
    capacityQueue.push({name, args, key, resolve, reject})
    runCapacityQueue()
  })
}

export function killWorker() {
  if (libxertWorker) {
    libxertWorker.terminate()
    libxertWorker = null
  }
}

export function getWorker() {
  if (!libxertWorker) {
    libxertWorker = new Worker(process.env.LIBXERT_WORKER_URL)

    const func = (name) => (...args) => new Promise((resolve, reject)=>{
      let prevWorker = libxertWorker
      const channel = new MessageChannel()
      channel.port1.onmessage = ({data}) => {
        if (libxertWorker != prevWorker) {
            // worker killed or re-created but message received from previous process?
            channel.port1.close()
            reject(data.error)
        } else if (data.error) {
            channel.port1.close()
            reject(data.error)
        } else if (data.result) {
            channel.port1.close()
            resolve(data.result)
        }
      }

      libxertWorker.postMessage({name, args}, [channel.port2]);

    })
      
    libxertWorkerDelegate = {
      // get_specificity: func('get_specificity'),
      // get_focus_duration: func('get_focus_duration'),
      // analyze_activity: func('analyze_activity'),
      analyze_race: func('analyze_race'),
      training_forecast(args, callback) {
        let prevWorker = libxertWorker
        return new Promise((resolve, reject)=>{
          const channel = new MessageChannel()
          channel.port1.onmessage = ({data}) => {
            if (libxertWorker != prevWorker) {
              // worker killed or re-created but message received from previous process?
              channel.port1.close()
              reject(data.error)
            } else if (data.error) {
              channel.port1.close()
              reject(data.error)
            } else if (data.result) {
              channel.port1.close()
              resolve(data.result)
            } else if (data.callback) {
              callback(data.callback)
              // NOTE: port1.postMessage doesn't work because asyncify is disabled in the wasm code, so it blocks the message loop.
              // channel.port1.postMessage({callback: result})
            }
          }

          libxertWorker.postMessage({name: 'training_forecast', args}, [channel.port2]);
        })
      }
      // sort_exercises: func('sort_exercises'),
      // recalculate: func('recalculate')
    }
  }
  return libxertWorkerDelegate
}
